import { httpCoreService } from "../services/HttpCoreService";
import { getStoredUser } from "../utils/auth";
import type {
  Study,
  CreateStudyRequest,
  UpdateStudyRequest,
  StudyFilters,
  StudyListResponse,
  StudyStatistics,
} from "../pages/StudyManagement/types/study.type";
import type { ApiResponse } from "../types/auth.type";

// Helper function to transform API response (snake_case) to frontend format (camelCase)
const transformStudyFromApi = (raw: unknown): Study => {
  const apiStudy = raw as Record<string, unknown>;
  const pickString = (...keys: string[]): string => {
    for (const key of keys) {
      const value = apiStudy[key];
      if (typeof value === "string") {
        return value;
      }
    }
    return "";
  };
  const pickBoolean = (...keys: string[]): boolean | undefined => {
    for (const key of keys) {
      const value = apiStudy[key];
      if (typeof value === "boolean") {
        return value;
      }
    }
    return undefined;
  };
  return {
    ...(raw as Study),
    sponsorName: pickString("sponsorName", "sponsor_name"),
    protocolNumber: pickString("protocolNumber", "protocol_number"),
    startDate: pickString("startDate", "start_date"),
    endDate: pickString("endDate", "end_date"),
    therapeuticArea: pickString("therapeuticArea", "therapeutic_area"),
    tenantId: pickString("tenantId", "tenant_id"),
    createdAt: pickString("createdAt", "created_at"),
    updatedAt: pickString("updatedAt", "updated_at"),
    siteAssignments:
      (apiStudy.siteAssignments as Study["siteAssignments"]) ??
      (apiStudy.site_assignments as Study["siteAssignments"]) ??
      [],
    principalInvestigator: pickString(
      "principalInvestigator",
      "principal_investigator",
    ),
    isArchived: pickBoolean("isArchived", "is_archived"),
    active: pickBoolean("active", "is_active"),
  };
};

export interface IStudyRepository {
  // Study management
  createStudy(study: CreateStudyRequest): Promise<Study>;
  getStudies(
    filters?: StudyFilters,
    page?: number,
    limit?: number,
  ): Promise<StudyListResponse>;
  getAllStudies(filters?: StudyFilters): Promise<Study[]>;
  getStudyById(id: string): Promise<Study>;
  updateStudy(id: string, updates: UpdateStudyRequest): Promise<Study>;
  deleteStudy(id: string): Promise<void>;
  activateStudy(id: string): Promise<Study>;
  archiveStudy(id: string): Promise<Study>;
  getStudyStatistics(): Promise<StudyStatistics>;
}

export class StudyRepository implements IStudyRepository {
  // Study management methods
  async createStudy(study: CreateStudyRequest): Promise<Study> {
    const response: ApiResponse<Study> = await httpCoreService.post<
      Study,
      CreateStudyRequest
    >("/studies", study);

    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) || "Failed to create study",
      );
    }

    if (!response.data) {
      throw new Error(
        "Study creation succeeded but no data was returned from the server.",
      );
    }

    return transformStudyFromApi(response.data);
  }

  async getStudies(
    filters?: StudyFilters,
    page = 1,
    limit = 10,
  ): Promise<StudyListResponse> {
    // Get tenant ID from logged-in user
    const user = getStoredUser();
    if (!user?.tenantId) {
      throw new Error("User not authenticated or tenant ID not found");
    }

    const queryParams = new URLSearchParams({
      tenant_id: user.tenantId,
      page: page.toString(),
      limit: limit.toString(),
    });

    // Add optional filters to query params (excluding tenant_id as it's automatically set)
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (
          key !== "tenant_id" &&
          value !== undefined &&
          value !== null &&
          value !== ""
        ) {
          queryParams.append(key, value.toString());
        }
      });
    }

    const response: ApiResponse<StudyListResponse> =
      await httpCoreService.get<StudyListResponse>(
        `/studies?${queryParams.toString()}`,
      );

    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) || "Failed to fetch studies",
      );
    }

    if (!response.data) {
      throw new Error(
        "Request succeeded but no data was returned from the server.",
      );
    }

    // Transform studies in the response
    return {
      ...response.data,
      studies: response.data.studies?.map(transformStudyFromApi) || [],
    };
  }

  async getAllStudies(filters?: StudyFilters): Promise<Study[]> {
    const allStudies: Study[] = [];
    let currentPage = 1;
    const limit = 100; // Fetch 100 studies per page for efficiency
    let hasMorePages = true;

    while (hasMorePages) {
      const response = await this.getStudies(filters, currentPage, limit);

      if (response.studies && response.studies.length > 0) {
        allStudies.push(...response.studies);

        // Calculate if there are more pages
        const totalPages = Math.ceil(response.total / limit);
        hasMorePages = currentPage < totalPages;
        currentPage++;
      } else {
        hasMorePages = false;
      }
    }

    return allStudies;
  }

  async getStudyById(id: string): Promise<Study> {
    const response: ApiResponse<Study> = await httpCoreService.get<Study>(
      `/studies/${id}`,
    );

    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) || "Failed to fetch study",
      );
    }

    if (!response.data) {
      throw new Error(
        "Request succeeded but no data was returned from the server.",
      );
    }

    return transformStudyFromApi(response.data);
  }

  async updateStudy(id: string, updates: UpdateStudyRequest): Promise<Study> {
    try {
      const response: ApiResponse<Study> = await httpCoreService.patch<
        Study,
        UpdateStudyRequest
      >(`/studies/${id}`, updates);

      const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

      if (!isSuccess) {
        throw new Error(
          (response.error ?? response.message) ||
            "Mahesh Failed to update study",
        );
      }

      if (!response.data) {
        throw new Error(
          "Study update succeeded but no data was returned from the server.",
        );
      }

      return transformStudyFromApi(response.data);
    } catch (error: unknown) {
      const err = error as {
        message?: string;
        response?: { data?: { message?: string } };
      };
      const message = err?.message ?? err?.response?.data?.message;
      throw new Error(message ?? "Failed to update study");
    }
  }

  async deleteStudy(id: string): Promise<void> {
    const response: ApiResponse<null> = await httpCoreService.delete<null>(
      `/studies/${id}`,
    );

    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) || "Failed to delete study",
      );
    }
  }

  async activateStudy(id: string): Promise<Study> {
    const response: ApiResponse<Study> = await httpCoreService.post<Study>(
      `/studies/${id}/activate`,
    );

    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) || "Failed to activate study",
      );
    }

    if (!response.data) {
      throw new Error(
        "Request succeeded but no data was returned from the server.",
      );
    }

    return transformStudyFromApi(response.data);
  }

  async archiveStudy(id: string): Promise<Study> {
    const response: ApiResponse<Study> = await httpCoreService.post<Study>(
      `/studies/${id}/archive`,
    );

    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) || "Failed to archive study",
      );
    }

    if (!response.data) {
      throw new Error(
        "Request succeeded but no data was returned from the server.",
      );
    }

    return transformStudyFromApi(response.data);
  }

  async getStudyStatistics(): Promise<StudyStatistics> {
    const response: ApiResponse<StudyStatistics> =
      await httpCoreService.get<StudyStatistics>("/studies/statistics");

    const isSuccess = response.statusCode >= 200 && response.statusCode < 300;

    if (!isSuccess) {
      throw new Error(
        (response.error ?? response.message) ||
          "Failed to fetch study statistics",
      );
    }

    if (!response.data) {
      throw new Error(
        "Request succeeded but no data was returned from the server.",
      );
    }

    return response.data;
  }
}

// Singleton instance
export const studyRepository = new StudyRepository();
